import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
    Container,
    Typography,
    TextField,
    Button,
    Grid,
    Paper,
    Box,
    CircularProgress,
    Autocomplete,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Avatar,
    Divider,
    Checkbox,
    FormGroup,
    FormControlLabel,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    FormHelperText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import HistoryIcon from '@mui/icons-material/History';
import DownloadIcon from '@mui/icons-material/Download';
import { useDropzone } from 'react-dropzone';
import { useDataContext } from '../DataContext'; // Import useDataContext
import { req, uploadImage, log } from '../utils'; // <-- Import req, uploadImage, and log (instead of logError)
import MachineHistoryDrawer from '../components/MachineHistoryDrawer'; // <-- Uncommented

// Import Lightbox
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

// Helper function to format Unix timestamp (seconds)
const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Invalid Date';
    try {
        const date = new Date(timestamp * 1000); // Multiply by 1000 if timestamp is in seconds
        return date.toLocaleString();
    } catch (e) {
        console.error("Error formatting timestamp:", e);
        return 'Invalid Date';
    }
};

// Tooltip Descriptions
const checkDescriptions = {
    lower: "Check crusher mechanism, belts, sensors, and base structure.",
    mid: "Inspect bin sensor, door mechanism, hinges, and internal wiring.",
    top: "Verify control board, user interface, top sensors, and lid operation.",
    cleaned: "Ensure machine exterior and interior are clean and free of debris."
};

const WAREHOUSE_FILTER_STORAGE_KEY = 'wipWarehouseFilter';

export default function WorkInProgress() {
    const { 
        machines, isLoadingMachines, machineError: machineListError, 
        parts, isLoadingParts, partsError, 
        warehouses, isLoadingWarehouses, warehouseError,
        updateMachineStatus, // <-- Get updateMachineStatus from context
        // refreshMachines // Removed unused variable
    } = useDataContext(); 
    console.log("warehouses", warehouses);
    const [selectedMachine, setSelectedMachine] = useState(null);
    const [repairData, setRepairData] = useState(null);
    const [isLoadingMachineData, setIsLoadingMachineData] = useState(false);
    const [error, setError] = useState('');
    const [selectedWarehouseFilter, setSelectedWarehouseFilter] = useState('all');
    const [currentRepairId, setCurrentRepairId] = useState(null); // <-- State for repair_id
    const [isSaving, setIsSaving] = useState(false); // <-- State for saving indicator
    const [showWarehouseFilter, setShowWarehouseFilter] = useState(false); // <-- Add state for filter visibility

    // Work Log State
    const [workNotes, setWorkNotes] = useState('');
    const [partsList, setPartsList] = useState([]); // List of { part: {}, quantity: number }
    const [currentPartSelection, setCurrentPartSelection] = useState(null); // For parts autocomplete
    const [currentPartQuantity, setCurrentPartQuantity] = useState(1);
    const [isMarkingReady, setIsMarkingReady] = useState(false);
    // State for Intake details fetched alongside repair data
    const [intakeNotes, setIntakeNotes] = useState('');
    const [intakeImages, setIntakeImages] = useState([]);

    // Add missing state declarations for intake time/user
    const [intakeTime, setIntakeTime] = useState(''); 
    const [intakeUserId, setIntakeUserId] = useState('');

    // Checkbox states
    const [lowerAssemblyChecked, setLowerAssemblyChecked] = useState(false);
    const [midAssemblyChecked, setMidAssemblyChecked] = useState(false);
    const [topAssemblyChecked, setTopAssemblyChecked] = useState(false);
    const [machineCleanedChecked, setMachineCleanedChecked] = useState(false);

    // Ref for debounce timer
    const saveTimeoutRef = useRef(null);

    // Drawer State - Uncommented
    const [drawerMachinePk, setDrawerMachinePk] = useState(null); 
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [drawerContent, setDrawerContent] = useState(null); 
    const [drawerData, setDrawerData] = useState(null);
    const [isLoadingDrawer, setIsLoadingDrawer] = useState(false);

    // Lightbox State
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [lightboxSlides, setLightboxSlides] = useState([]);

    // Add state for three photo categories
    const [earthingPhotos, setEarthingPhotos] = useState([]);
    const [earthingPhotoPaths, setEarthingPhotoPaths] = useState([]);
    const [earthingPhotoErrors, setEarthingPhotoErrors] = useState({});
    const [isUploadingEarthing, setIsUploadingEarthing] = useState(false);

    const [cabinetPhotos, setCabinetPhotos] = useState([]);
    const [cabinetPhotoPaths, setCabinetPhotoPaths] = useState([]);
    const [cabinetPhotoErrors, setCabinetPhotoErrors] = useState({});
    const [isUploadingCabinet, setIsUploadingCabinet] = useState(false);

    const [completePhotos, setCompletePhotos] = useState([]);
    const [completePhotoPaths, setCompletePhotoPaths] = useState([]);
    const [completePhotoErrors, setCompletePhotoErrors] = useState({});
    const [isUploadingComplete, setIsUploadingComplete] = useState(false);

    // Log context data for debugging
    useEffect(() => {
        console.log("WIP - Initial Machines in context:", machines);
        console.log("WIP - Initial Warehouses in context:", warehouses);
    }, [machines, warehouses]);

    // Load filter from localStorage on mount
    useEffect(() => {
        const savedFilter = localStorage.getItem(WAREHOUSE_FILTER_STORAGE_KEY);
        if (savedFilter) {
            setSelectedWarehouseFilter(savedFilter);
        }
    }, []);

    // Save filter to localStorage when it changes
    useEffect(() => {
        localStorage.setItem(WAREHOUSE_FILTER_STORAGE_KEY, selectedWarehouseFilter);
    }, [selectedWarehouseFilter]);

    const handleMachineSelect = (event, newValue) => {
        // Clear existing save timer if machine changes
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        setSelectedMachine(newValue);
        setRepairData(null);
        setCurrentRepairId(null); // <-- Reset repair_id
        setError('');
        setWorkNotes('');
        setPartsList([]);
        setLowerAssemblyChecked(false);
        setMidAssemblyChecked(false);
        setTopAssemblyChecked(false);
        setMachineCleanedChecked(false);
        setIntakeNotes('');
        setIntakeImages([]);

        if (newValue) {
            fetchMachineData(newValue.machine_id_pk);
        }
    };

    // Function to fetch specific machine details AND existing repair data
    const fetchMachineData = async (machineIdPk) => {
        if (!machineIdPk) return;
        setIsLoadingMachineData(true);
        setError('');
        setRepairData(null);
        setCurrentRepairId(null); // Reset just in case
        // Reset intake info state
        setIntakeNotes(''); 
        setIntakeImages([]); 
        setIntakeTime(''); // <-- Add state for intake time
        setIntakeUserId(''); // <-- Add state for intake user ID (or name later)

        try {
            // --- Fetch Existing Repair Data (and potentially Intake Info) --- 
            const repairEndpoint = `get_repairs_data?machine_id=${machineIdPk}`;
            console.log(`WIP - Fetching repair data from: /api/warehouse/${repairEndpoint}`);
            const fetchedRepairData = await req('get', repairEndpoint);
            console.log("WIP - Raw Fetched Repair Data:", fetchedRepairData);

            // Check if data is valid (not empty array or false)
            if (fetchedRepairData && typeof fetchedRepairData === 'object' && Object.keys(fetchedRepairData).length > 0) {
                setRepairData(fetchedRepairData); 
                setCurrentRepairId(fetchedRepairData.repair_id || null); 

                // -- Populate Intake Info (Parse intake_information JSON) --
                setIntakeTime(fetchedRepairData.intake_time || ''); // Store intake time
                setIntakeUserId(fetchedRepairData.intake_user_id || ''); // Store intake user ID
                let intakeInfo = {};
                if (typeof fetchedRepairData.intake_information === 'string') {
                    try {
                        intakeInfo = JSON.parse(fetchedRepairData.intake_information);
                        setIntakeNotes(intakeInfo.notes || 'No intake notes recorded.');
                        // Handle images: could be string or array
                        const images = intakeInfo.images;
                        if (typeof images === 'string') {
                            setIntakeImages([images]); // Convert single string to array
                        } else if (Array.isArray(images)) {
                            setIntakeImages(images);
                        } else {
                            setIntakeImages([]);
                        }
                        console.log("WIP - Parsed Intake Info:", intakeInfo);
                    } catch (parseError) {
                        console.error("WIP - Failed to parse intake_information JSON:", parseError);
                        setIntakeNotes("Error parsing intake details.");
                        setIntakeImages([]);
                    }
                } else {
                    setIntakeNotes('No intake details found.');
                    setIntakeImages([]);
                }
                // -- End Intake Info Population --
                
                let details = {};
                // Parse the repair details JSON string
                if (typeof fetchedRepairData.details === 'string') {
                    try {
                        details = JSON.parse(fetchedRepairData.details);
                        console.log("WIP - Parsed repair details:", details);
                    } catch (parseError) {
                        console.error("WIP - Failed to parse repair details JSON:", parseError);
                        setError("Failed to read existing repair details.");
                        details = {}; // Reset to empty if parsing fails
                    }
                } else {
                    console.log("WIP - Repair details field was not a string:", fetchedRepairData.details);
                }

                // Populate repair form fields using the PARSED details object
                setWorkNotes(details.notes || '');
                setPartsList(details.parts_used || []); 
                // --- Updated Parts Loading --- 
                const loadedParts = (details.parts_used || []).map(p => {
                    // Find the full part data from context using the primary key stored in p.part_id
                    const fullPart = parts.find(contextPart => contextPart.part_id_pk === p.part_id);
                    // console.log(`WIP - Mapping loaded part PK ${p.part_id}. Found in context:`, fullPart);
                    return {
                        part: fullPart ? { // If found in context, use full data
                            id: fullPart.part_id_pk, // Use PK as the main ID for consistency
                            name: fullPart.name,
                            part_id_display: fullPart.part_id, // Store display ID from context part
                            imageUrl: fullPart.imageUrl
                        } : { // Fallback if part not found in context
                            id: p.part_id, // Use the ID from storage as fallback ID
                            name: `Unknown Part (PK: ${p.part_id})`, // Indicate it's unknown
                            part_id_display: 'N/A',
                            imageUrl: ''
                        },
                        quantity: p.qty
                    };
                });
                console.log("WIP - Setting PartsList state with loaded parts:", loadedParts);
                setPartsList(loadedParts); 
                // --- End Updated Parts Loading --- 
                setLowerAssemblyChecked(details.check_values?.check_lower || false); 
                setMidAssemblyChecked(details.check_values?.check_mid || false); 
                setTopAssemblyChecked(details.check_values?.check_top || false); 
                setMachineCleanedChecked(details.check_values?.check_cleaned || false); 

                // Load the three photo arrays from details if present
                setEarthingPhotoPaths(details.earthing_photos || []);
                setCabinetPhotoPaths(details.clean_cabinet_photos || []);
                setCompletePhotoPaths(details.machine_complete_photos || []);

            } else {
                console.log("WIP - No existing repair data found or received invalid data. Assuming new repair.");
                // Reset fields if no repair data
                setIntakeNotes('No intake details found for this machine.');
                setIntakeImages([]);
                setIntakeTime('');
                setIntakeUserId('');
                // Reset repair-specific fields
                setWorkNotes('');
                setPartsList([]);
                setLowerAssemblyChecked(false);
                setMidAssemblyChecked(false);
                setTopAssemblyChecked(false);
                setMachineCleanedChecked(false);
            }

        } catch (err) {
            console.error('WIP - Error fetching repair/intake details:', err);
            setError('Failed to load repair/intake details.');
            setRepairData(null);
            setCurrentRepairId(null);
            setIntakeNotes('');
            setIntakeImages([]);
            setIntakeTime('');
            setIntakeUserId('');
        } finally {
            setIsLoadingMachineData(false);
        }
    };

    // Wrap handleAutoSave in useCallback
    const handleAutoSave = useCallback(async () => {
        if (!selectedMachine?.machine_id_pk || isLoadingMachineData || isSaving) {
            return;
        }
        console.log("WIP - handleAutoSave triggered"); // Log trigger

        // --- Updated Payload Construction --- 
        // Log the current partsList state right before mapping
        console.log("WIP - Current partsList state before mapping:", JSON.stringify(partsList, null, 2));

        const partsPayload = partsList.map(item => ({
            part_id: item.part.id, // Use item.part.id which consistently holds the PK
            qty: item.quantity
        }));
        console.log("WIP - Parts Payload for Save:", partsPayload); // Log parts payload

        const currentDetailsPayload = {
            notes: workNotes,
            earthing_photos: earthingPhotoPaths,
            clean_cabinet_photos: cabinetPhotoPaths,
            machine_complete_photos: completePhotoPaths,
            parts_used: partsPayload,
            check_values: {
                check_lower: lowerAssemblyChecked,
                check_mid: midAssemblyChecked,
                check_top: topAssemblyChecked,
                check_cleaned: machineCleanedChecked
            }
        };

        // --- Updated Comparison Logic --- 
        let initialDetailsPayload = { notes: '', parts_used: [], check_values: {}, earthing_photos: [], clean_cabinet_photos: [], machine_complete_photos: [] };
        if (repairData && typeof repairData.details === 'string') {
            try {
                const parsedInitial = JSON.parse(repairData.details);
                initialDetailsPayload = {
                    notes: parsedInitial.notes || '',
                    // Normalize initial parts_used to match current structure for comparison
                    parts_used: (parsedInitial.parts_used || []).map(p => ({ part_id: p.part_id, qty: p.qty })).sort((a,b) => a.part_id - b.part_id),
                    check_values: parsedInitial.check_values || { check_lower: false, check_mid: false, check_top: false, check_cleaned: false },
                    earthing_photos: parsedInitial.earthing_photos || [],
                    clean_cabinet_photos: parsedInitial.clean_cabinet_photos || [],
                    machine_complete_photos: parsedInitial.machine_complete_photos || []
                };
            } catch (e) {
                log('WIP - Error parsing initial details for comparison:', e);
                // Proceed assuming no initial data if parsing fails
            }
        }
        console.log("WIP - Initial Details for Comparison:", initialDetailsPayload);
        console.log("WIP - Current Details for Comparison:", currentDetailsPayload);

        // Create comparable version of current parts and images
        const currentPartsComparable = [...currentDetailsPayload.parts_used].sort((a,b) => a.part_id - b.part_id); // Sort for comparison
        // Compare each photo array
        const earthingPhotosChanged = JSON.stringify(currentDetailsPayload.earthing_photos) !== JSON.stringify(initialDetailsPayload.earthing_photos);
        const cabinetPhotosChanged = JSON.stringify(currentDetailsPayload.clean_cabinet_photos) !== JSON.stringify(initialDetailsPayload.clean_cabinet_photos);
        const completePhotosChanged = JSON.stringify(currentDetailsPayload.machine_complete_photos) !== JSON.stringify(initialDetailsPayload.machine_complete_photos);

        // Compare individual fields
        const notesChanged = currentDetailsPayload.notes !== initialDetailsPayload.notes;
        const partsChanged = JSON.stringify(currentPartsComparable) !== JSON.stringify(initialDetailsPayload.parts_used);
        const checksChanged = (
            currentDetailsPayload.check_values.check_lower !== (initialDetailsPayload.check_values?.check_lower || false) ||
            currentDetailsPayload.check_values.check_mid !== (initialDetailsPayload.check_values?.check_mid || false) ||
            currentDetailsPayload.check_values.check_top !== (initialDetailsPayload.check_values?.check_top || false) ||
            currentDetailsPayload.check_values.check_cleaned !== (initialDetailsPayload.check_values?.check_cleaned || false)
        );

        if (!notesChanged && !earthingPhotosChanged && !cabinetPhotosChanged && !completePhotosChanged && !partsChanged && !checksChanged) {
            console.log("WIP - Auto-save skipped: No changes detected.");
            return; // Don't save if nothing changed
        }
        console.log("WIP - Changes detected, proceeding with auto-save.", { notesChanged, earthingPhotosChanged, cabinetPhotosChanged, completePhotosChanged, partsChanged, checksChanged });
        // --- End Updated Comparison Logic ---

        setIsSaving(true);
        console.log(`Auto-saving repair data for machine PK: ${selectedMachine.machine_id_pk}, repair ID: ${currentRepairId}`);

        // Construct main payload (now uses currentDetailsPayload without parts_used modification)
        const payload = {
            machine_id: selectedMachine.machine_id_pk,
            details: JSON.stringify(currentDetailsPayload), // Pass the whole current state
            ...(currentRepairId && { repair_id: currentRepairId })
        };

        console.log("Auto-save Payload (Final):", payload);

        try {
            const result = await req('post', 'save_repairs_data', payload);
            console.log("Auto-save Result:", result);
            let newRepairId = currentRepairId;
            if (typeof result === 'number' && result > 0) {
                newRepairId = result;
                setCurrentRepairId(result); 
                console.log("Updated currentRepairId to:", result);
            } else if (result === false || result?.success === false) {
                 console.error("Auto-save failed. API returned:", result);
                 setError("Failed to auto-save changes."); 
                 setIsSaving(false); // Ensure saving indicator is turned off on failure
                 return; 
            }

            // Update repairData state to reflect the saved state
            setRepairData(prevData => ({
                ...(prevData || {}),
                repair_id: newRepairId,
                details: JSON.stringify(currentDetailsPayload) // Store the just-saved details string
            }));
            console.log("Updated local repairData reference after successful save.");

        } catch (err) {
            log('Error during auto-save:', err);
            setError("Error auto-saving changes."); 
        } finally {
            // Check if still mounted before setting state (optional but good practice)
            setIsSaving(false);
        }
    // Keep dependencies - the improved comparison handles state changes
    }, [selectedMachine, isLoadingMachineData, isSaving, currentRepairId, workNotes, earthingPhotoPaths, cabinetPhotoPaths, completePhotoPaths, partsList, lowerAssemblyChecked, midAssemblyChecked, topAssemblyChecked, machineCleanedChecked, repairData]); // Added parts to dep array as it's used in fetchMachineData mapping

    // useEffect hook to trigger debounced save
    useEffect(() => {
        if (isLoadingMachineData || !selectedMachine) {
            return;
        }
        // Clear previous timer
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Set new timer
        saveTimeoutRef.current = setTimeout(() => {
            handleAutoSave();
        }, 3000); // 3 second delay

        // Cleanup timer on unmount or dependency change
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    // Remove uploadedWorkPhotoPaths from dependencies
    // Save is triggered by manual edits; handleAutoSave reads the current paths
    }, [handleAutoSave, workNotes, partsList, lowerAssemblyChecked, midAssemblyChecked, topAssemblyChecked, machineCleanedChecked, selectedMachine, isLoadingMachineData]);

    // Add drop handlers for each photo category
    const handleDrop = (setter, setPath, setError, setUploading) => async (acceptedFiles) => {
        setter(prev => [...prev, ...acceptedFiles]);
        setUploading(true);
        setError({});
        const newPaths = [];
        const errors = {};
        for (const file of acceptedFiles) {
            try {
                const result = await uploadImage('add_servicing_image', file);
                if (result && result.success && result.file_name) {
                    newPaths.push(result.file_name);
                } else {
                    errors[file.name] = result?.error || 'Upload failed';
                }
            } catch (err) {
                errors[file.name] = err.message || 'Network error or other issue';
            }
        }
        setPath(prev => [...prev, ...newPaths]);
        setError(prev => ({...prev, ...errors}));
        setUploading(false);
    };
    const onDropEarthing = handleDrop(setEarthingPhotos, setEarthingPhotoPaths, setEarthingPhotoErrors, setIsUploadingEarthing);
    const onDropCabinet = handleDrop(setCabinetPhotos, setCabinetPhotoPaths, setCabinetPhotoErrors, setIsUploadingCabinet);
    const onDropComplete = handleDrop(setCompletePhotos, setCompletePhotoPaths, setCompletePhotoErrors, setIsUploadingComplete);

    // Parts Management
    const handleAddPart = () => {
        if (currentPartSelection && currentPartQuantity > 0) {
            const existingPartIndex = partsList.findIndex(item => item.part.id === currentPartSelection.id);
            if (existingPartIndex > -1) {
                 const updatedList = [...partsList];
                 updatedList[existingPartIndex].quantity += currentPartQuantity;
                 setPartsList(updatedList);
            } else {
                 // Use the full part object from the Autocomplete selection
                 setPartsList([...partsList, { part: currentPartSelection, quantity: currentPartQuantity }]);
            }
            setCurrentPartSelection(null);
            setCurrentPartQuantity(1);
        }
    };

    const handleRemovePart = (partIdToRemove) => {
        setPartsList(partsList.filter(item => item.part.id !== partIdToRemove));
    };

    // Handler for the Mark As Ready button
    const handleMarkAsReady = async () => {
        // Double-check conditions just before calling
        if (!canMarkAsReady || !currentRepairId || !selectedMachine?.machine_id_pk) {
            setError("Cannot complete repair: ensure all checks are done, notes exist, and repair has been saved at least once.");
            return;
        }

        // Ensure latest changes are saved before completing
        // Clear any existing timer and save immediately
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        await handleAutoSave(); // Await the save
        
        // Wait a very short moment to ensure save state update propagates if needed
        await new Promise(resolve => setTimeout(resolve, 100)); 
        if (isSaving) { // If save is still somehow in progress, wait a bit longer
             console.warn("Waiting for save to finish before completing...");
             await new Promise(resolve => setTimeout(resolve, 500)); 
        }

        setIsMarkingReady(true);
        setError('');
        console.log(`Marking repair ${currentRepairId} for machine ${selectedMachine.machine_id_pk} as complete.`);

        try {
            const payload = { repair_id: currentRepairId };
            const result = await req('post', 'complete_repair', payload);
            console.log("Complete Repair API Result:", result);

            if (result === true) { // Check for explicit true
                alert('Machine service complete and marked Ready for Deployment!');
                
                // Update status in local context
                updateMachineStatus(selectedMachine.machine_id_pk, 'ready_to_deploy');

                // Reset the form/view
                setSelectedMachine(null);
                // Other state resets happen in handleMachineSelect which is triggered by setting selectedMachine to null

            } else {
                console.error('Failed to mark repair as complete via API:', result);
                setError('Server failed to mark repair as complete. Please try again.');
            }

        } catch (err) {
            console.error('Error calling complete_repair API:', err);
            setError('Failed to update machine status. Please try again.');
        } finally {
            setIsMarkingReady(false);
        }
    };

    // Helper function to create label with tooltip
    const createLabelWithTooltip = (text, description) => (
        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
            {text}
            <Tooltip title={description} placement="top">
                <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: '1rem', color: 'action.active' }} />
            </Tooltip>
        </Box>
    );

    // --- Filter machines based on selected warehouse ---
    const machineOptions = useMemo(() => {
        if (!machines) return [];
        console.log("WIP - Machines:", machines);
        const wearhouseVenueIds = warehouses.map(warehouse => warehouse.venue_id);
        console.log("WIP - Wearhouse Venue IDs:", wearhouseVenueIds);
        const filteredMachines = machines
            .filter(machine => machine.machine_status === 'servicing') // Only show 'servicing' machines
            .filter(machine => wearhouseVenueIds.includes(machine.venue_id)) // Only show machines that are in a warehouse (have venue_id)
            .filter(machine => {
                // Apply warehouse filter only if a specific warehouse is selected
                return selectedWarehouseFilter === 'all' || String(machine.venue_id) === String(selectedWarehouseFilter);
            })
            .map(machine => ({
                label: `${machine.serial_id} - ${machine.machine_type || 'N/A'}  (${machine.venue_name || 'No Location'})`,
                id: machine.machine_id_pk, // Use primary key for ID
                machine_id_pk: machine.machine_id_pk, // Pass PK through
                machine_status: machine.machine_status, // Pass status
                venue_id: machine.venue_id // Pass venue_id
            }))
            .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically

        console.log(`Filtered Machines for Warehouse '${selectedWarehouseFilter}':`, filteredMachines);
        return filteredMachines;

    }, [machines, selectedWarehouseFilter]);

    // Format parts for Autocomplete
    const partOptions = parts.map(p => ({
        label: `${p.name} (${p.part_id})`,
        id: p.part_id_pk, // Use primary key for value/comparison
        name: p.name,
        part_id_display: p.part_id,
        imageUrl: p.imageUrl || '/path/to/placeholder.jpg' // Use real URL when available
    }));

    const handleWarehouseFilterChange = (event) => {
        const newFilterValue = event.target.value;
        console.log("WIP - Warehouse filter changed to:", newFilterValue);
        setSelectedWarehouseFilter(newFilterValue);
        setSelectedMachine(null); // Reset machine selection when filter changes
        setRepairData(null);
        setError('');
    };

    // Find the full data for the selected machine from the context
    const selectedMachineFullData = selectedMachine 
        ? machines.find(m => m.machine_id_pk === selectedMachine.machine_id_pk) 
        : null;

    // Determine if the button should be enabled
    const canMarkAsReady = 
        !isMarkingReady && 
        selectedMachineFullData?.machine_status === 'servicing' &&
        workNotes.trim().length > 0 &&
        lowerAssemblyChecked &&
        midAssemblyChecked &&
        topAssemblyChecked &&
        machineCleanedChecked;

    // Drawer Handlers - Uncommented and updated API endpoint
    
    const handleOpenDrawer = async (machinePk, contentType) => {
        console.log(`WIP - Opening drawer for machine PK ${machinePk}, content: ${contentType}`);
        setDrawerMachinePk(machinePk);
        setDrawerContent(contentType);
        setIsDrawerOpen(true);
        setIsLoadingDrawer(true);
        setDrawerData(null); // Clear previous data
    
        // Use the specific endpoint provided by the user for history
        const endpoint = contentType === 'history' 
            ? `get_machine_history?machine_id=${machinePk}` 
            : `get_machine_notes?machine_id=${machinePk}`; // Assuming a notes endpoint might exist
            
        console.log(`WIP - Drawer fetching from: /api/warehouse/${endpoint}`);
        
        try {
            // Make sure 'req' function handles the base path correctly
            const data = await req('get', endpoint); 
            console.log("WIP - Drawer data received:", data);
            // Basic validation: Check if data is not null/undefined
            if (data !== null && data !== undefined) {
                 setDrawerData(data); // Assuming API returns the data needed
            } else {
                 console.warn("WIP - Drawer data received null or undefined.");
                 setDrawerData({ error: `No ${contentType} data found or invalid response.` });
            }
        } catch (error) { 
            log(`Error fetching drawer ${contentType}:`, error);
            setDrawerData({ error: `Failed to load ${contentType}. Check network request.` }); // Show error in drawer
        } finally {
            setIsLoadingDrawer(false);
        }
    };
    
    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
        setDrawerMachinePk(null);
        setDrawerContent(null);
        setDrawerData(null);
    };
    

    // --- Effect to clear selected machine if its status changes away from 'servicing' ---
    useEffect(() => {
        if (selectedMachine && selectedMachine.machine_status !== 'servicing') {
            const updatedMachine = machines.find(m => m.machine_id_pk === selectedMachine.machine_id_pk);
            if (!updatedMachine || updatedMachine.machine_status !== 'servicing') {
                 setSelectedMachine(null);
                 setWorkNotes('');
                 setPartsList([]);
            }
        }
    }, [machines, selectedMachine]);

    // --- Handle Change Machine Button Click --- 
    const handleChangeMachine = () => {
        // Clear all work-in-progress state when changing machine
        if (saveTimeoutRef.current) { // Clear pending save
            clearTimeout(saveTimeoutRef.current);
        }
        setSelectedMachine(null);
        setRepairData(null);
        setCurrentRepairId(null);
        setError('');
        setWorkNotes('');
        setPartsList([]);
        setLowerAssemblyChecked(false);
        setMidAssemblyChecked(false);
        setTopAssemblyChecked(false);
        setMachineCleanedChecked(false);
        setIntakeNotes('');
        setIntakeImages([]);
    };

    // Lightbox Handlers
    const openLightbox = (imagePaths, index) => {
        console.log("Opening lightbox for paths:", imagePaths, "at index:", index);
        const slides = imagePaths
            .filter(path => path && typeof path === 'string') // Ensure path is a valid string
            .map(path => ({
                src: `https://app.bottlecycler.com/api/warehouse/get_img/?img_url=${encodeURIComponent(path.trim())}`
            }));
        
        if (slides.length > 0) {
            setLightboxSlides(slides);
            setLightboxIndex(index < slides.length ? index : 0); // Ensure index is valid
            setLightboxOpen(true);
        } else {
            console.warn("No valid image paths found to open lightbox.");
        }
    };

    // Add dropzone hooks for each
    const dropzoneEarthing = useDropzone({ onDrop: onDropEarthing, accept: { 'image/*': [] } });
    const dropzoneCabinet = useDropzone({ onDrop: onDropCabinet, accept: { 'image/*': [] } });
    const dropzoneComplete = useDropzone({ onDrop: onDropComplete, accept: { 'image/*': [] } });

    // Add delete handlers for each photo array
    const handleDeleteEarthingPhoto = (photoPath) => setEarthingPhotoPaths(prev => prev.filter(path => path !== photoPath));
    const handleDeleteCabinetPhoto = (photoPath) => setCabinetPhotoPaths(prev => prev.filter(path => path !== photoPath));
    const handleDeleteCompletePhoto = (photoPath) => setCompletePhotoPaths(prev => prev.filter(path => path !== photoPath));

    // Add download handler
    const handleDownloadPhoto = (photoPath) => {
        const url = `https://app.bottlecycler.com/api/warehouse/get_img/?img_url=${encodeURIComponent(photoPath.trim())}`;
        const link = document.createElement('a');
        link.href = url;
        link.download = photoPath.split('/').pop() || 'photo.jpg';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>

            {/* --- Step 1: Show Selector OR Header --- */} 
            {!selectedMachine ? (
                // --- Show Machine Selector Area --- 
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        {/* --- Machine Selection Autocomplete --- */}
                        <Grid item xs={12} md={showWarehouseFilter ? 6 : 9}> {/* Adjust width based on filter visibility */}
                            {isLoadingMachines && <CircularProgress />} 
                            {machineListError && !isLoadingMachines && (
                                <Typography color="error" sx={{ mb: 2 }}>Error loading machine list: {machineListError}</Typography>
                            )}
                            {!isLoadingMachines && !machineListError && (
                                <Autocomplete
                                    id="machine-select-wip"
                                    options={machineOptions} // Use filtered options
                                    getOptionLabel={(option) => option.label || ""}
                                    value={selectedMachine} // Should be null here
                                    onChange={handleMachineSelect}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params}
                                            label="Select Machine"
                                            required
                                            // InputProps removed for simplicity, add back if needed
                                        />
                                    )}
                                    disabled={isLoadingMachines} // Disable while loading list
                                    noOptionsText={isLoadingMachines ? "Loading..." : (selectedWarehouseFilter !== 'all' ? "No machines match filter in this warehouse" : "No 'Servicing' machines found")}
                                />
                            )}
                        </Grid>
                        {/* --- Toggle Filter Button --- */}
                        <Grid item xs={12} md={3}>
                             <Button 
                                 variant="outlined" 
                                 onClick={() => setShowWarehouseFilter(prev => !prev)}
                                 startIcon={<FilterListIcon />}
                                 fullWidth 
                                 sx={{ height: '56px' }} // Match Autocomplete height
                             >
                                 {showWarehouseFilter ? 'Hide Filter' : 'Filter by Warehouse'}
                             </Button>
                        </Grid>
                        {/* --- Warehouse Filter Dropdown (Conditional) --- */}
                        {showWarehouseFilter && (
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth error={!!warehouseError && !isLoadingWarehouses}>
                                    <InputLabel id="warehouse-filter-label">Warehouse</InputLabel>
                                    <Select
                                        labelId="warehouse-filter-label"
                                        id="warehouse-filter-select"
                                        value={selectedWarehouseFilter}
                                        label="Warehouse"
                                        onChange={handleWarehouseFilterChange}
                                        disabled={isLoadingWarehouses}
                                    >
                                        <MenuItem value="all"><em>All Warehouses</em></MenuItem>
                                {warehouses.map((wh) => (
                                    <MenuItem key={wh.venue_id} value={String(wh.venue_id)}>
                                        {wh.venue_name}
                                    </MenuItem>
                                ))}
                                    </Select>
                                    {warehouseError && <FormHelperText>Error loading warehouses</FormHelperText>}
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            ) : (
                // --- Show Header when Machine is Selected --- 
                <Paper sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                     <Typography variant="h6">Working on: {selectedMachine.label}</Typography>
                     <Box sx={{ display: 'flex', alignItems: 'center'}}> {/* Wrap buttons/icons in a Box */} 
                         {/* History Icon Button */}
                         <Tooltip title="View History">
                             <IconButton 
                                 onClick={() => handleOpenDrawer(selectedMachine.machine_id_pk, 'history')} 
                                 sx={{ mr: 1 }} // Add margin to the right
                                 color="primary" // Use theme primary color
                             >
                                 <HistoryIcon />
                             </IconButton>
                         </Tooltip>
                         <Button variant="text" onClick={handleChangeMachine} sx={{ ml: 1 }}>
                             Change Machine
                         </Button>
                     </Box>
                </Paper>
            )}
            
            {/* Removed loading indicator from here */} 
            {/* Error display specific to this page/selection - moved down */}
            {/* {error && !selectedMachine && (
                <Typography color="error" sx={{mt: 1}}>{error}</Typography>
            )} */} 

            {/* --- Step 2: Machine Details & Work Log Form (Conditional) --- */} 
            {selectedMachine && (
                <Paper sx={{ p: 3 }}>
                    {/* Show loading indicator ONLY when fetching this specific machine's data */} 
                    {isLoadingMachineData ? (
                        <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
                             <CircularProgress size={30} />
                             <Typography>Loading repair data...</Typography>
                         </Box>
                    ) : (
                        // --- Show Form ONLY when NOT loading --- 
                        <>
                            {/* --- Display Intake Info --- */} 
                            <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Intake Details (Recorded: {intakeTime ? formatTimestamp(intakeTime) : 'N/A'} by User: {intakeUserId || 'N/A'})
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}> {/* Preserve whitespace in notes */} 
                                    <strong>Notes:</strong> {intakeNotes || 'N/A'}
                                </Typography>
                                <Typography variant="subtitle2">Images:</Typography>
                                {intakeImages.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                        {intakeImages.map((path, index) => {
                                            if (!path || typeof path !== 'string') return null;
                                            const intakeImageUrl = `https://app.bottlecycler.com/api/warehouse/get_img/?img_url=${encodeURIComponent(path.trim())}`;
                                            return (
                                                <Avatar 
                                                    key={`intake-${path}-${index}`}
                                                    variant="rounded" 
                                                    src={intakeImageUrl} 
                                                    alt={`Intake image ${index + 1}`}
                                                    sx={{ width: 64, height: 64, bgcolor: 'grey.200', cursor: 'pointer' }} // Add cursor pointer
                                                    onClick={() => openLightbox(intakeImages, index)} // <-- Use lightbox handler
                                                />
                                            );
                                        })}
                                    </Box>
                                ) : (
                                    <Typography variant="caption">No intake images recorded.</Typography>
                                )}
                            </Box>
                            
                            <Divider sx={{ my: 2 }} />

                            {/* --- Work Log Section --- */} 
                
                            <Grid container spacing={3}>
                                {/* Work Notes - Indicate Saving State */} 
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id="workNotes"
                                        label="Work Notes"
                                        multiline
                                        rows={4}
                                        value={workNotes}
                                        onChange={(e) => setWorkNotes(e.target.value)}
                                        helperText={isSaving ? "Auto-saving..." : " "}
                                        FormHelperTextProps={{ sx: { minHeight: '1.2em' } }}
                                    />
                                </Grid>

                                {/* Photo Upload - Display uploaded and pending */} 
                                <Grid item xs={12} sx={{ mt: 1 }}>
                                    <Typography variant="subtitle1" gutterBottom>Add Earthing Photos</Typography>
                                    <Box {...dropzoneEarthing.getRootProps()} sx={{ border: '2px dashed grey', borderRadius: 1, p: 2, textAlign: 'center', cursor: 'pointer', bgcolor: dropzoneEarthing.isDragActive ? 'action.hover' : 'background.paper' }}>
                                        <input {...dropzoneEarthing.getInputProps()} />
                                        <p>{dropzoneEarthing.isDragActive ? 'Drop photos here...' : 'Drag \u2018n\u2019 drop photos, or click to select'}</p>
                                    </Box>
                                    {(earthingPhotoPaths.length > 0 || earthingPhotos.length > 0 || Object.keys(earthingPhotoErrors).length > 0 || isUploadingEarthing) && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2">Files:</Typography>
                                            <List dense>
                                                {earthingPhotoPaths.map((path, index) => {
                                                    const earthingImgUrl = `https://app.bottlecycler.com/api/warehouse/get_img/?img_url=${encodeURIComponent(path.trim())}`;
                                                    return (
                                                        <ListItem key={`uploaded-earthing-${index}-${path}`}
                                                            secondaryAction={
                                                                <>
                                                                    <IconButton edge="end" aria-label="download" onClick={() => handleDownloadPhoto(path)}><DownloadIcon /></IconButton>
                                                                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteEarthingPhoto(path)}><DeleteIcon /></IconButton>
                                                                </>
                                                            }
                                                        >
                                                            <Avatar variant="rounded" src={earthingImgUrl} alt={path.split('/').pop()} sx={{ width: 56, height: 56, mr: 2, bgcolor: 'grey.200', cursor: 'pointer' }} />
                                                            <ListItemText primary={path.split('/').pop() || path} secondary="Uploaded" secondaryTypographyProps={{ color: 'success.main' }} />
                                                        </ListItem>
                                                    );
                                                })}
                                                {earthingPhotos.filter(f => !earthingPhotoPaths.some(up => up.includes(f.name))).map((file, index) => (
                                                    <ListItem key={`pending-earthing-${index}-${file.name}`}>
                                                        <ListItemText 
                                                            primary={file.name} 
                                                            secondary={earthingPhotoErrors[file.name] ? `Error: ${earthingPhotoErrors[file.name]}` : (isUploadingEarthing ? 'Uploading...' : 'Pending')} 
                                                            secondaryTypographyProps={{ color: earthingPhotoErrors[file.name] ? 'error' : 'text.secondary' }}
                                                        />
                                                        {isUploadingEarthing && !earthingPhotoErrors[file.name] && <CircularProgress size={20} sx={{ ml: 1 }}/>} 
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}
                                </Grid>
                                <Grid item xs={12} sx={{ mt: 1 }}>
                                    <Typography variant="subtitle1" gutterBottom>Clean Cabinet Photos</Typography>
                                    <Box {...dropzoneCabinet.getRootProps()} sx={{ border: '2px dashed grey', borderRadius: 1, p: 2, textAlign: 'center', cursor: 'pointer', bgcolor: dropzoneCabinet.isDragActive ? 'action.hover' : 'background.paper' }}>
                                        <input {...dropzoneCabinet.getInputProps()} />
                                        <p>{dropzoneCabinet.isDragActive ? 'Drop photos here...' : 'Drag \u2018n\u2019 drop photos, or click to select'}</p>
                                    </Box>
                                    {(cabinetPhotoPaths.length > 0 || cabinetPhotos.length > 0 || Object.keys(cabinetPhotoErrors).length > 0 || isUploadingCabinet) && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2">Files:</Typography>
                                            <List dense>
                                                {cabinetPhotoPaths.map((path, index) => {
                                                    const cabinetImgUrl = `https://app.bottlecycler.com/api/warehouse/get_img/?img_url=${encodeURIComponent(path.trim())}`;
                                                    return (
                                                        <ListItem key={`uploaded-cabinet-${index}-${path}`}
                                                            secondaryAction={
                                                                <>
                                                                    <IconButton edge="end" aria-label="download" onClick={() => handleDownloadPhoto(path)}><DownloadIcon /></IconButton>
                                                                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteCabinetPhoto(path)}><DeleteIcon /></IconButton>
                                                                </>
                                                            }
                                                        >
                                                            <Avatar variant="rounded" src={cabinetImgUrl} alt={path.split('/').pop()} sx={{ width: 56, height: 56, mr: 2, bgcolor: 'grey.200', cursor: 'pointer' }} />
                                                            <ListItemText primary={path.split('/').pop() || path} secondary="Uploaded" secondaryTypographyProps={{ color: 'success.main' }} />
                                                        </ListItem>
                                                    );
                                                })}
                                                {cabinetPhotos.filter(f => !cabinetPhotoPaths.some(up => up.includes(f.name))).map((file, index) => (
                                                    <ListItem key={`pending-cabinet-${index}-${file.name}`}>
                                                        <ListItemText 
                                                            primary={file.name} 
                                                            secondary={cabinetPhotoErrors[file.name] ? `Error: ${cabinetPhotoErrors[file.name]}` : (isUploadingCabinet ? 'Uploading...' : 'Pending')} 
                                                            secondaryTypographyProps={{ color: cabinetPhotoErrors[file.name] ? 'error' : 'text.secondary' }}
                                                        />
                                                        {isUploadingCabinet && !cabinetPhotoErrors[file.name] && <CircularProgress size={20} sx={{ ml: 1 }}/>} 
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}
                                </Grid>
                                <Grid item xs={12} sx={{ mt: 1 }}>
                                    <Typography variant="subtitle1" gutterBottom>Machine Complete Photo</Typography>
                                    <Box {...dropzoneComplete.getRootProps()} sx={{ border: '2px dashed grey', borderRadius: 1, p: 2, textAlign: 'center', cursor: 'pointer', bgcolor: dropzoneComplete.isDragActive ? 'action.hover' : 'background.paper' }}>
                                        <input {...dropzoneComplete.getInputProps()} />
                                        <p>{dropzoneComplete.isDragActive ? 'Drop photos here...' : 'Drag \u2018n\u2019 drop photos, or click to select'}</p>
                                    </Box>
                                    {(completePhotoPaths.length > 0 || completePhotos.length > 0 || Object.keys(completePhotoErrors).length > 0 || isUploadingComplete) && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2">Files:</Typography>
                                            <List dense>
                                                {completePhotoPaths.map((path, index) => {
                                                    const completeImgUrl = `https://app.bottlecycler.com/api/warehouse/get_img/?img_url=${encodeURIComponent(path.trim())}`;
                                                    return (
                                                        <ListItem key={`uploaded-complete-${index}-${path}`}
                                                            secondaryAction={
                                                                <>
                                                                    <IconButton edge="end" aria-label="download" onClick={() => handleDownloadPhoto(path)}><DownloadIcon /></IconButton>
                                                                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteCompletePhoto(path)}><DeleteIcon /></IconButton>
                                                                </>
                                                            }
                                                        >
                                                            <Avatar variant="rounded" src={completeImgUrl} alt={path.split('/').pop()} sx={{ width: 56, height: 56, mr: 2, bgcolor: 'grey.200', cursor: 'pointer' }} />
                                                            <ListItemText primary={path.split('/').pop() || path} secondary="Uploaded" secondaryTypographyProps={{ color: 'success.main' }} />
                                                        </ListItem>
                                                    );
                                                })}
                                                {completePhotos.filter(f => !completePhotoPaths.some(up => up.includes(f.name))).map((file, index) => (
                                                    <ListItem key={`pending-complete-${index}-${file.name}`}>
                                                        <ListItemText 
                                                            primary={file.name} 
                                                            secondary={completePhotoErrors[file.name] ? `Error: ${completePhotoErrors[file.name]}` : (isUploadingComplete ? 'Uploading...' : 'Pending')} 
                                                            secondaryTypographyProps={{ color: completePhotoErrors[file.name] ? 'error' : 'text.secondary' }}
                                                        />
                                                        {isUploadingComplete && !completePhotoErrors[file.name] && <CircularProgress size={20} sx={{ ml: 1 }}/>} 
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}
                                </Grid>

                                {/* Parts Used Section */} 
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>Parts Used</Typography>
                                    {/* Handle Parts Loading/Error */} 
                                    {isLoadingParts && <CircularProgress size={20} sx={{ mr: 1 }} /> } 
                                    {partsError && !isLoadingParts && (
                                         <Typography color="error" variant="caption" display="block" sx={{ mb: 1 }}>Error loading parts list: {partsError}</Typography>
                                     )}
                                    <Grid container spacing={2} alignItems="stretch">
                                        <Grid item xs={12} sm={6} md={7}>
                                            <Autocomplete
                                                id="parts-select"
                                                options={partOptions}
                                                value={currentPartSelection}
                                                onChange={(event, newValue) => { setCurrentPartSelection(newValue); }}
                                                getOptionLabel={(option) => option.label || ""}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                renderOption={(props, option) => (
                                                    <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props} key={option.id}>
                                                        <Avatar src={option.imageUrl} alt="" sx={{ mr: 1, width: 24, height: 24, bgcolor: 'grey.300'}} /> 
                                                        {option.label}
                                                    </Box>
                                                )}
                                                renderInput={(params) => <TextField {...params} label="Select Part" />}
                                                disabled={isLoadingParts || partsError}
                                            />
                                        </Grid>
                                         <Grid item xs={6} sm={3} md={2}>
                                             <TextField
                                                label="Quantity"
                                                type="number"
                                                value={currentPartQuantity}
                                                onChange={(e) => setCurrentPartQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                                InputProps={{ inputProps: { min: 1 } }}
                                                fullWidth
                                                disabled={!currentPartSelection}
                                            />
                                         </Grid>
                                         <Grid item xs={6} sm={3} md={3}>
                                             <Button 
                                                 variant="outlined"
                                                 onClick={handleAddPart}
                                                 disabled={!currentPartSelection || currentPartQuantity <= 0}
                                                 startIcon={<AddCircleOutlineIcon />}
                                                 fullWidth
                                                 color="persiangreen"
                                                 sx={{ height: '100%' }}
                                             >
                                                 Add Part
                                             </Button>
                                         </Grid>
                                    </Grid>

                                    {/* List of Added Parts */} 
                                    {partsList.length > 0 && (
                                        <Paper variant="outlined" sx={{ mt: 2, mb: 2 }}>
                                            <List dense>
                                                {partsList.map((item, index) => (
                                                    <ListItem 
                                                        key={item.part.id || `part-${index}`}
                                                        secondaryAction={ <IconButton edge="end" aria-label="delete" onClick={() => handleRemovePart(item.part.id)}> <DeleteIcon /> </IconButton> }
                                                    >
                                                         <Avatar src={item.part.imageUrl} alt="" sx={{ mr: 1.5, width: 28, height: 28, bgcolor: 'grey.300' }} /> 
                                                        <ListItemText primary={`${item.part.name} (${item.part.part_id_display})`} secondary={`Quantity: ${item.quantity}`} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Paper>
                                    )}
                                </Grid>

                                {/* --- Checkboxes Section --- */} 
                                <Grid item xs={12} sx={{ mt: 1 }}>
                                    <Typography variant="h6" gutterBottom>Checks</Typography>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} sm={6}>
                                                <FormGroup>
                                                    <FormControlLabel control={<Checkbox checked={lowerAssemblyChecked} onChange={(e) => setLowerAssemblyChecked(e.target.checked)} />} label={createLabelWithTooltip("Lower assembly checked", checkDescriptions.lower)} />
                                                    <FormControlLabel control={<Checkbox checked={midAssemblyChecked} onChange={(e) => setMidAssemblyChecked(e.target.checked)} />} label={createLabelWithTooltip("Mid assembly checked", checkDescriptions.mid)} />
                                                </FormGroup>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormGroup>
                                                    <FormControlLabel control={<Checkbox checked={topAssemblyChecked} onChange={(e) => setTopAssemblyChecked(e.target.checked)} />} label={createLabelWithTooltip("Top assembly checked", checkDescriptions.top)} />
                                                    <FormControlLabel control={<Checkbox checked={machineCleanedChecked} onChange={(e) => setMachineCleanedChecked(e.target.checked)} />} label={createLabelWithTooltip("Machine cleaned", checkDescriptions.cleaned)} />
                                                </FormGroup>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12}><Divider sx={{ my: 2 }} /></Grid>

                                {/* Mark as Ready Button */} 
                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="persiangreen"
                                        disabled={!canMarkAsReady} 
                                        onClick={handleMarkAsReady}
                                        size="large"
                                    >
                                        {isMarkingReady ? <CircularProgress size={24} /> : 'Mark Service Complete (Ready for Deployment)'}
                                    </Button>
                                </Grid>
                                {/* Error display moved inside the conditional rendering block */} 
                                {error && (
                                    <Grid item xs={12} sx={{ mt: 1 }}>
                                        <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert> 
                                    </Grid>
                                 )}
                            </Grid>
                        </>
                    )}
                </Paper>
            )}

            {/* Placeholder if no machine selected and not loading initial list */} 
            {!selectedMachine && !isLoadingMachines && !machineListError && !error && (
                <Typography align="center" sx={{ color: 'text.secondary', mt: 4 }}>
                    Select a machine from the list above to log work.
                </Typography>
            )}
            
            {/* Save and Mark Ready Buttons - REMOVED */}
            {/* ... */}
            {/* Conditionally render the Alert below the main content - Removed, handled above */}
            {/* {error && (
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            )} */} 

            {/* Drawer for History/Notes - Uncommented */}
            {drawerMachinePk && (
                 <MachineHistoryDrawer 
                     open={isDrawerOpen}
                     onClose={handleCloseDrawer}
                     machinePk={drawerMachinePk}
                     contentType={drawerContent}
                     data={drawerData}
                     loading={isLoadingDrawer}
                     machineLabel={selectedMachine?.label || `Machine PK: ${drawerMachinePk}`} // Pass label for title
                 />
            )}

            {/* Render Lightbox */} 
            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={lightboxSlides}
                index={lightboxIndex}
            />

        </Container>
    );
} 